/**
 * RecentActivityList - Display recent audit logs in timeline format
 *
 * Features:
 * - Timeline layout with vertical line connecting items
 * - Icon for each action type
 * - User info and action description
 * - Entity links (clickable)
 * - Time ago formatting
 * - "View All Logs" button at bottom
 * - Empty state with Activity icon
 * - Staggered animations on load
 */
import { Card, CardContent, CardHeader, CardTitle, Button } from '@respectly/ui';
import { cn } from '@respectly/ui';
import {
  Activity,
  User,
  Shield,
  Settings,
  CreditCard,
  Mail,
  FileText,
  Trash2,
  Edit,
  Plus,
  ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import type { SuperAdminAuditLog } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface RecentActivityListProps {
  logs: SuperAdminAuditLog[];
  isLoading: boolean;
  onViewAll?: () => void;
}

// Action icon mapping
const ACTION_ICONS: Record<string, React.ElementType> = {
  create: Plus,
  update: Edit,
  delete: Trash2,
  login: User,
  logout: User,
  suspend: Shield,
  reactivate: Shield,
  'update-plan': CreditCard,
  'send-email': Mail,
  'create-document': FileText,
  'update-settings': Settings,
};

// Action color mapping
const ACTION_COLORS: Record<string, string> = {
  create: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30',
  update: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
  delete: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
  login: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800',
  logout: 'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800',
  suspend: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  reactivate: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30',
};

// ============================================
// Helper Functions
// ============================================

function getActionIcon(action: string): React.ElementType {
  return ACTION_ICONS[action] || Activity;
}

function getActionColor(action: string): string {
  return (
    ACTION_COLORS[action] ||
    'text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800'
  );
}

function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getActionDescription(log: SuperAdminAuditLog): string {
  const { action, entityType, details } = log;

  // Create descriptive text based on action and entity
  const entityName = details?.entityName || entityType;

  switch (action) {
    case 'create':
      return `Created ${entityType} "${entityName}"`;
    case 'update':
      return `Updated ${entityType} "${entityName}"`;
    case 'delete':
      return `Deleted ${entityType} "${entityName}"`;
    case 'suspend':
      return `Suspended ${entityType} "${entityName}"`;
    case 'reactivate':
      return `Reactivated ${entityType} "${entityName}"`;
    default:
      return `${action} ${entityType}`;
  }
}

// ============================================
// Component
// ============================================

export function RecentActivityList({ logs, isLoading, onViewAll }: RecentActivityListProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return <RecentActivityListSkeleton />;
  }

  const handleViewAll = () => {
    if (onViewAll) {
      onViewAll();
    } else {
      navigate({ to: '/audit' });
    }
  };

  if (logs.length === 0) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="text-center py-8 text-slate-500 dark:text-slate-400">
            <Activity className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No recent activity to display</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {logs.map((log, index) => {
            const Icon = getActionIcon(log.action);
            const colorClass = getActionColor(log.action);

            return (
              <motion.div
                key={log.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
              >
                {/* Icon */}
                <div className={cn('flex-shrink-0 p-2 rounded-lg', colorClass)}>
                  <Icon className="h-4 w-4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 dark:text-white font-medium">
                    {getActionDescription(log)}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    by {log.userId || 'System'} • {formatTimeAgo(log.createdAt)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewAll}
            className="w-full text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          >
            View All Logs
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function RecentActivityListSkeleton() {
  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60">
      <CardHeader className="pb-3">
        <div className="h-6 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="h-10 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-3 w-1/2 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
